import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Pencil, Linkedin, Globe } from "lucide-react";
import { STATUS_META, VERIFY_META } from "./utils";

const VerifyCell = ({ v, id }) => (
  <td className="p-3 text-xs max-w-xs" data-testid={`lead-verify-${id}`}>
    {!v ? <span className="text-slate-400">—</span> : (
      <div className="space-y-1" title={v.reason}>
        <Badge variant="outline" className={VERIFY_META[v.verdict].cls} data-testid={`lead-verify-badge-${id}`}>
          {VERIFY_META[v.verdict].label}{v.business_type ? ` · ${v.business_type}` : ""}
        </Badge>
        {v.quote && <p className="italic text-slate-600 line-clamp-3">„{v.quote}“</p>}
        {!v.quote && <p className="text-slate-500 line-clamp-2">{v.reason}</p>}
        {v.url && <a href={v.url} target="_blank" rel="noreferrer" className="text-[#0052CC] hover:underline" data-testid={`lead-verify-link-${id}`}>Πηγή ↗</a>}
      </div>
    )}
  </td>
);

export const LeadRow = ({ lead, busy, onStatus, onEdit }) => {
  const st = STATUS_META[lead.status];
  const id = lead.lead_id;
  return (
    <tr className="border-b border-slate-100 align-top hover:bg-slate-50/60 transition-colors duration-150" data-testid={`agency-lead-row-${id}`}>
      <td className="p-3">
        <div className="font-medium text-slate-900">
          {lead.company_name}
          {lead.source === "web" && <Badge variant="outline" className="ml-1.5 text-[10px] border-sky-200 text-sky-700" data-testid={`lead-source-web-${id}`}>Internet</Badge>}
        </div>
        <div className="text-xs text-slate-500">{lead.company_country} · {lead.company_city || "—"}</div>
        {lead.company_website && (
          <a href={lead.company_website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0052CC] hover:underline">
            <Globe className="w-3 h-3" /> {lead.company_domain}
          </a>
        )}
      </td>
      <td className="p-3">
        <div className="text-slate-900">{lead.first_name} {lead.last_name}</div>
        <div className="text-xs text-slate-600">{lead.job_title}</div>
        {lead.person_linkedin && (
          <a href={lead.person_linkedin} target="_blank" rel="noreferrer" className="flex w-fit items-center gap-1 text-xs text-[#0052CC] hover:underline" data-testid={`lead-linkedin-${id}`}>
            <Linkedin className="w-3 h-3" /> LinkedIn
          </a>
        )}
        {lead.company_linkedin && (
          <a href={lead.company_linkedin} target="_blank" rel="noreferrer" className="flex w-fit items-center gap-1 text-xs text-slate-600 hover:underline" data-testid={`lead-company-linkedin-${id}`}>
            <Linkedin className="w-3 h-3" /> Εταιρεία
          </a>
        )}
      </td>
      <td className="p-3 text-xs font-mono text-slate-700" data-testid={`lead-emails-${id}`}>
        {(lead.company_emails || []).slice(0, 3).map((e) => <div key={e}>{e}</div>)}
        {!(lead.company_emails || []).length && <span className="text-slate-400">—</span>}
      </td>
      <td className="p-3 text-xs font-mono text-slate-700" data-testid={`lead-phone-${id}`}>
        {lead.company_phone || <span className="text-slate-400">{lead.enriched ? "δεν βρέθηκε" : "—"}</span>}
      </td>
      <VerifyCell v={lead.verify} id={id} />
      <td className="p-3 text-xs text-slate-700 max-w-sm" data-testid={`lead-whyfit-${id}`}>
        <p className="line-clamp-4">{lead.why_fit || <span className="text-slate-400">—</span>}</p>
      </td>
      <td className="p-3">
        <Badge variant="outline" className={st.cls} data-testid={`lead-status-${id}`}>{st.label}</Badge>
      </td>
      <td className="p-3">
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" disabled={busy} className="h-8 w-8 text-emerald-600" onClick={() => onStatus(lead, "approved")} data-testid={`lead-approve-${id}`}><Check className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={busy} className="h-8 w-8 text-rose-600" onClick={() => onStatus(lead, "rejected")} data-testid={`lead-reject-${id}`}><X className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={busy} className="h-8 w-8" onClick={() => onEdit(lead)} data-testid={`lead-edit-${id}`}><Pencil className="w-4 h-4" /></Button>
        </div>
      </td>
    </tr>
  );
};
